import toast from 'react-hot-toast';
import {
  LinkedinShareButton,
  FacebookShareButton,
  WhatsappShareButton,
  EmailShareButton,
  LinkedinIcon,
  FacebookIcon,
  WhatsappIcon,
  EmailIcon,
} from 'react-share';

export interface ShareContent {
  title: string;
  description?: string;
  url?: string;
  imageUrl?: string;
  hashtags?: string[];
  // LinkedIn Add to Profile fields
  certificateId?: string;
  organizationId?: string;
  issueDate?: string; // ISO date string
  expirationDate?: string; // ISO date string
}

export interface SocialShareProps {
  content: ShareContent;
  variant?: 'buttons' | 'icons' | 'compact';
  className?: string;
  platforms?: ('linkedin' | 'facebook' | 'whatsapp' | 'email')[];
  onShare?: (platform: string) => void;
  showAddToLinkedIn?: boolean; // When true, LinkedIn button will add to profile instead of share
}

const platformConfig = {
  linkedin: {
    name: 'LinkedIn',
    ShareButton: LinkedinShareButton,
    Icon: LinkedinIcon,
    gradient: 'from-[#0A66C2] to-[#004182]',
    hoverGradient: 'hover:from-[#004182] hover:to-[#003366]',
    bgColor: '#0A66C2',
  },
  facebook: {
    name: 'Facebook',
    ShareButton: FacebookShareButton,
    Icon: FacebookIcon,
    gradient: 'from-[#1877F2] to-[#0d65d9]',
    hoverGradient: 'hover:from-[#0d65d9] hover:to-[#0a4fa8]',
    bgColor: '#1877F2',
  },
  whatsapp: {
    name: 'WhatsApp',
    ShareButton: WhatsappShareButton,
    Icon: WhatsappIcon,
    gradient: 'from-[#25D366] to-[#128C7E]',
    hoverGradient: 'hover:from-[#128C7E] hover:to-[#075E54]',
    bgColor: '#25D366',
  },
  email: {
    name: 'Email',
    ShareButton: EmailShareButton,
    Icon: EmailIcon,
    gradient: 'from-gray-600 to-gray-700',
    hoverGradient: 'hover:from-gray-700 hover:to-gray-800',
    bgColor: '#6B7280',
  },
};

export default function SocialShare({
  content,
  variant = 'buttons',
  className = '',
  platforms = ['linkedin', 'facebook', 'whatsapp', 'email'],
  onShare,
  showAddToLinkedIn = false,
}: SocialShareProps) {

  const { title: rawTitle, description: rawDescription, url, hashtags, certificateId, organizationId, issueDate, expirationDate } = content;
  const shareUrl = url || window.location.href;

  // Decode HTML entities (e.g. &amp; → &)
  const decodeHtml = (text: string) => {
    const el = document.createElement('textarea');
    el.innerHTML = text;
    return el.value;
  };
  const title = decodeHtml(rawTitle);
  const description = rawDescription ? decodeHtml(rawDescription) : undefined;

  // Build LinkedIn Add to Profile URL
  const buildLinkedInAddToProfileUrl = () => {
    const params = new URLSearchParams();
    params.append('startTask', 'CERTIFICATION_NAME');
    params.append('name', title);

    if (organizationId) {
      params.append('organizationId', organizationId);
    }

    if (issueDate) {
      const date = new Date(issueDate);
      params.append('issueYear', date.getFullYear().toString());
      params.append('issueMonth', (date.getMonth() + 1).toString());
    }

    if (expirationDate) {
      const expDate = new Date(expirationDate);
      params.append('expirationYear', expDate.getFullYear().toString());
      params.append('expirationMonth', (expDate.getMonth() + 1).toString());
    }

    if (certificateId) {
      params.append('certId', certificateId);
    }

    if (shareUrl) {
      params.append('certUrl', shareUrl);
    }

    return `https://www.linkedin.com/profile/add?${params.toString()}`;
  };

  // Handle Add to LinkedIn Profile click
  const handleAddToLinkedIn = () => {
    const linkedInUrl = buildLinkedInAddToProfileUrl();
    window.open(linkedInUrl, '_blank', 'width=600,height=700');
    onShare?.('linkedin-add-profile');
  };

  // Handle LinkedIn share as post (fallback when no organizationId)
  // LinkedIn no longer supports pre-filling post text via URL params.
  // Copy suggested post text to clipboard, then open official share dialog.
  const handleLinkedInSharePost = async () => {
    const hashtagStr = hashtags?.length ? hashtags.map(tag => `#${tag.replace(/^#/, '')}`).join(' ') : '';
    const postText = [
      `I earned a new certificate: ${title}!`,
      description || null,
      hashtagStr || null,
    ].filter(Boolean).join('\n\n');

    try {
      await navigator.clipboard.writeText(postText);
      toast('Post text copied! Paste it in LinkedIn', { icon: '📋', duration: 4000 });
    } catch {
      // Clipboard may fail silently
    }

    const linkedInShareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
    window.open(linkedInShareUrl, '_blank', 'width=600,height=700');
    onShare?.('linkedin');
  };

  // Helper to build share props for each platform
  const getShareProps = (platform: string) => {
    const baseProps = { url: shareUrl };

    switch (platform) {
      case 'linkedin':
        return {
          ...baseProps,
          title,
          summary: description,
        };

      case 'facebook':
        return {
          ...baseProps,
          hashtag: hashtags?.[0] ? `#${hashtags[0].replace(/^#/, '')}` : undefined,
          quote: description ? `${title}\n\n${description}` : title,
        };

      case 'whatsapp':
        const whatsappMessage = [
          title,
          description,
        ].filter(Boolean).join('\n\n');
        return {
          ...baseProps,
          title: whatsappMessage,
          separator: '\n\n',
        };

      case 'email':
        const emailBody = [
          `I'd like to share my achievement with you.`,
          title,
          description || null,
        ].filter(Boolean).join('\n\n');
        return {
          ...baseProps,
          subject: `Certificate: ${title}`,
          body: emailBody,
        };

      default:
        return baseProps;
    }
  };

  const handleBeforeShare = async (platform: string) => {
    onShare?.(platform);
  };

  // Render variants
  if (variant === 'icons') {
    return (
      <div className={`flex items-center justify-center gap-3 ${className}`}>
        {platforms.map((platform) => {
          const config = platformConfig[platform];
          const ShareButton = config.ShareButton;
          const Icon = config.Icon;
          const shareProps = getShareProps(platform);

          // For LinkedIn with Add to Profile functionality
          if (platform === 'linkedin' && showAddToLinkedIn && organizationId) {
            return (
              <div key={platform} className="group relative">
                <button
                  onClick={handleAddToLinkedIn}
                  className="!p-0 !m-0 !flex !items-center !justify-center bg-transparent border-none"
                >
                  <div className={`p-3 rounded-full bg-gradient-to-br ${config.gradient} ${config.hoverGradient} transition-all duration-300 hover:scale-110 hover:shadow-lg shadow-md cursor-pointer`}>
                    <Icon size={20} round bgStyle={{ fill: 'transparent' }} iconFillColor="white" />
                  </div>
                </button>
                <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 text-xs font-medium text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                  {config.name}
                </span>
              </div>
            );
          }

          // LinkedIn share as post (no organizationId)
          if (platform === 'linkedin' && showAddToLinkedIn && !organizationId) {
            return (
              <div key={platform} className="group relative">
                <button
                  onClick={handleLinkedInSharePost}
                  className="!p-0 !m-0 !flex !items-center !justify-center bg-transparent border-none"
                >
                  <div className={`p-3 rounded-full bg-gradient-to-br ${config.gradient} ${config.hoverGradient} transition-all duration-300 hover:scale-110 hover:shadow-lg shadow-md cursor-pointer`}>
                    <Icon size={20} round bgStyle={{ fill: 'transparent' }} iconFillColor="white" />
                  </div>
                </button>
                <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 text-xs font-medium text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                  {config.name}
                </span>
              </div>
            );
          }

          return (
            <div key={platform} className="group relative">
              <ShareButton
                {...shareProps}
                beforeOnClick={() => handleBeforeShare(platform)}
                className="!p-0 !m-0 !flex !items-center !justify-center"
              >
                <div className={`p-3 rounded-full bg-gradient-to-br ${config.gradient} ${config.hoverGradient} transition-all duration-300 hover:scale-110 hover:shadow-lg shadow-md cursor-pointer`}>
                  <Icon size={20} round bgStyle={{ fill: 'transparent' }} iconFillColor="white" />
                </div>
              </ShareButton>
              <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 text-xs font-medium text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                {config.name}
              </span>
            </div>
          );
        })}
      </div>
    );
  }

  if (variant === 'compact') {
    return (
      <div className={`flex items-center justify-center gap-2 ${className}`}>
        {platforms.map((platform) => {
          const config = platformConfig[platform];
          const ShareButton = config.ShareButton;
          const Icon = config.Icon;
          const shareProps = getShareProps(platform);

          // For LinkedIn with Add to Profile functionality
          if (platform === 'linkedin' && showAddToLinkedIn && organizationId) {
            return (
              <button
                key={platform}
                onClick={handleAddToLinkedIn}
                className="!p-0 !m-0 bg-transparent border-none"
              >
                <div className="p-2.5 rounded-xl hover:scale-105 transition-all duration-200 cursor-pointer">
                  <Icon size={32} round bgStyle={{ fill: config.bgColor }} />
                </div>
              </button>
            );
          }

          // LinkedIn share as post (no organizationId)
          if (platform === 'linkedin' && showAddToLinkedIn && !organizationId) {
            return (
              <button
                key={platform}
                onClick={handleLinkedInSharePost}
                className="!p-0 !m-0 bg-transparent border-none"
              >
                <div className="p-2.5 rounded-xl hover:scale-105 transition-all duration-200 cursor-pointer">
                  <Icon size={32} round bgStyle={{ fill: config.bgColor }} />
                </div>
              </button>
            );
          }

          return (
            <ShareButton
              key={platform}
              {...shareProps}
              beforeOnClick={() => handleBeforeShare(platform)}
              className="!p-0 !m-0"
            >
              <div className="p-2.5 rounded-xl hover:scale-105 transition-all duration-200 cursor-pointer">
                <Icon size={32} round bgStyle={{ fill: config.bgColor }} />
              </div>
            </ShareButton>
          );
        })}
      </div>
    );
  }

  // Default: buttons variant
  return (
    <div className={`space-y-3 ${className}`}>
      {/* Share Buttons Grid */}
      <div className="grid grid-cols-2 gap-3">
        {platforms.map((platform) => {
          const config = platformConfig[platform];
          const ShareButton = config.ShareButton;
          const Icon = config.Icon;
          const shareProps = getShareProps(platform);

          // For LinkedIn with Add to Profile functionality
          if (platform === 'linkedin' && showAddToLinkedIn && organizationId) {
            return (
              <button
                key={platform}
                onClick={handleAddToLinkedIn}
                className="!p-0 !m-0 !w-full bg-transparent border-none"
              >
                <div className={`group flex items-center justify-center gap-2.5 p-3.5 rounded-xl bg-gradient-to-br ${config.gradient} ${config.hoverGradient} text-white font-semibold transition-all duration-300 hover:scale-[1.02] hover:shadow-lg shadow-md cursor-pointer w-full`}>
                  <Icon size={20} round bgStyle={{ fill: 'transparent' }} iconFillColor="white" />
                  <span>{config.name}</span>
                </div>
              </button>
            );
          }

          // LinkedIn share as post (no organizationId)
          if (platform === 'linkedin' && showAddToLinkedIn && !organizationId) {
            return (
              <button
                key={platform}
                onClick={handleLinkedInSharePost}
                className="!p-0 !m-0 !w-full bg-transparent border-none"
              >
                <div className={`group flex items-center justify-center gap-2.5 p-3.5 rounded-xl bg-gradient-to-br ${config.gradient} ${config.hoverGradient} text-white font-semibold transition-all duration-300 hover:scale-[1.02] hover:shadow-lg shadow-md cursor-pointer w-full`}>
                  <Icon size={20} round bgStyle={{ fill: 'transparent' }} iconFillColor="white" />
                  <span>{config.name}</span>
                </div>
              </button>
            );
          }

          return (
            <ShareButton
              key={platform}
              {...shareProps}
              beforeOnClick={() => handleBeforeShare(platform)}
              className="!p-0 !m-0 !w-full"
            >
              <div className={`group flex items-center justify-center gap-2.5 p-3.5 rounded-xl bg-gradient-to-br ${config.gradient} ${config.hoverGradient} text-white font-semibold transition-all duration-300 hover:scale-[1.02] hover:shadow-lg shadow-md cursor-pointer w-full`}>
                <Icon size={20} round bgStyle={{ fill: 'transparent' }} iconFillColor="white" />
                <span>{config.name}</span>
              </div>
            </ShareButton>
          );
        })}
      </div>
    </div>
  );
}
